import {
  Hamburger,
  makeStyles,
  NavCategory,
  NavCategoryItem,
  NavDrawer,
  NavDrawerBody,
  NavDrawerFooter,
  NavDrawerHeader,
  NavItem,
  NavSectionHeader,
  NavSubItem,
  NavSubItemGroup,
  Tooltip,
  useRestoreFocusTarget,
} from '@fluentui/react-components';
import {
  bundleIcon,
  ClipboardCheckmark20Filled,
  ClipboardCheckmark20Regular,
  Home20Filled,
  Home20Regular,
  List20Filled,
  List20Regular,
  Merge20Filled,
  Merge20Regular,
  Settings20Filled,
  Settings20Regular,
  ShieldTask20Filled,
  ShieldTask20Regular,
  Wrench20Filled,
  Wrench20Regular,
} from '@fluentui/react-icons';
import { useLocation, useNavigate } from '@tanstack/react-router';

import { useAuth } from '../lib/auth/hooks';
import { canReviewUnconfirmed, isSignedInRole, settingsSectionAllowed } from '../lib/auth/roles';
import { useI18n } from '../lib/i18n/hooks';
import { TAGS_UI_ENABLED } from '../lib/uiFlags';
import { AppVersion } from './AppVersion';

const useStyles = makeStyles({
  subSectionHeader: {
    paddingLeft: '36px',
  },
});

// Bundled icons for nav items
const HomeIcon = bundleIcon(Home20Filled, Home20Regular);
const ServiceRequestsIcon = bundleIcon(Wrench20Filled, Wrench20Regular);
const ItemsIcon = bundleIcon(List20Filled, List20Regular);
const SettingsIcon = bundleIcon(Settings20Filled, Settings20Regular);
const AdminIcon = bundleIcon(ShieldTask20Filled, ShieldTask20Regular);
const MergeIcon = bundleIcon(Merge20Filled, Merge20Regular);
const UnconfirmedGroupsIcon = bundleIcon(ClipboardCheckmark20Filled, ClipboardCheckmark20Regular);

interface AppNavigationProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  type?: 'overlay' | 'inline';
}

export function AppNavigation({ isOpen, onOpenChange, type = 'overlay' }: AppNavigationProps): React.ReactElement {
  const styles = useStyles();
  const { data: session } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const restoreFocusTargetAttributes = useRestoreFocusTarget();

  const isAdmin = session?.user.role === 'admin';
  const isAuthenticated = isSignedInRole(session?.user.role);
  const canMerge = isSignedInRole(session?.user.role);
  const canReview = canReviewUnconfirmed(session);
  const role = session?.user.role;

  const getSelectedValue = (): string => {
    const path = location.pathname;
    // Editing an item/group can be reached from different places; when the
    // unconfirmed-groups flow links here (?from=admin-unconfirmed) keep that
    // menu entry highlighted instead of the generic "Items" entry.
    const from = (location.search as { from?: string }).from;
    if (path === '/') return 'home';
    if (path.startsWith('/service-requests')) return 'service-requests';
    if (path.startsWith('/items')) return from === 'admin-unconfirmed' ? 'unconfirmed-groups' : 'items';
    if (path.startsWith('/merge-suggestions')) return 'merge-suggestions';
    if (path.startsWith('/unconfirmed-groups')) return 'unconfirmed-groups';
    // Settings sub-pages
    if (path === '/settings/switch-organization') return 'settings-switch-organization';
    if (path === '/settings/locations') return 'settings-locations';
    if (path === '/settings/category') return 'settings-category';
    if (path.startsWith('/settings/attributes')) return 'settings-attributes';
    if (path === '/settings/brand') return 'settings-brand';
    if (path === '/settings/product-line') return 'settings-product-line';
    if (path === '/settings/tags') return 'settings-tags';
    if (path.startsWith('/settings')) return 'settings';
    // Admin sub-pages
    if (path === '/admin/api-keys') return 'admin-api-keys';
    if (path === '/admin/feature-flags') return 'admin-feature-flags';
    if (path === '/admin/permissions') return 'admin-permissions';
    if (path === '/admin/ai-metrics') return 'admin-ai-metrics';
    if (path === '/admin/inventory-overview') return 'inventory-overview';
    if (path.startsWith('/admin/algolia')) return 'admin-algolia';
    if (path.startsWith('/admin')) return 'admin';
    return '';
  };

  const getSelectedCategoryValue = (): string => {
    const path = location.pathname;
    if (path.startsWith('/settings')) return 'settings-category';
    if (path.startsWith('/admin')) return 'admin-category';
    return '';
  };

  const handleNavigate = (to: string) => {
    if (type === 'overlay') {
      onOpenChange(false);
    }
    void navigate({ to });
  };

  return (
    <NavDrawer
      open={isOpen}
      type={type}
      onOpenChange={(_, data) => onOpenChange(data.open)}
      selectedValue={getSelectedValue()}
      selectedCategoryValue={getSelectedCategoryValue()}
    >
      <NavDrawerHeader>
        <Tooltip content={t('navigation.closeMenu', 'Close navigation')} relationship="label">
          <Hamburger onClick={() => onOpenChange(false)} {...restoreFocusTargetAttributes} />
        </Tooltip>
      </NavDrawerHeader>
      <NavDrawerBody>
        <NavItem icon={<HomeIcon />} value="home" onClick={() => handleNavigate('/')}>
          {t('common.appName')}
        </NavItem>

        {isAuthenticated && (
          <>
            <NavSectionHeader>{t('navigation.navigation', 'Navigation')}</NavSectionHeader>
            <NavItem
              icon={<ServiceRequestsIcon />}
              value="service-requests"
              onClick={() => handleNavigate('/service-requests')}
            >
              {t('navigation.service-requests')}
            </NavItem>
            <NavItem icon={<ItemsIcon />} value="items" onClick={() => handleNavigate('/items')}>
              {t('navigation.items')}
            </NavItem>
            {canMerge && (
              <NavItem
                icon={<MergeIcon />}
                value="merge-suggestions"
                onClick={() => handleNavigate('/merge-suggestions')}
              >
                {t('admin.merge-suggestions.title', 'Merge suggestions')}
              </NavItem>
            )}
            {canReview && (
              <NavItem
                icon={<UnconfirmedGroupsIcon />}
                value="unconfirmed-groups"
                onClick={() => handleNavigate('/unconfirmed-groups')}
              >
                {t('admin.unconfirmed-groups.title', 'Unconfirmed Asset Groups')}
              </NavItem>
            )}

            {/* Settings Category with Sub-Items */}
            <NavCategory value="settings-category">
              <NavCategoryItem icon={<SettingsIcon />}>{t('navigation.settings', 'Settings')}</NavCategoryItem>
              <NavSubItemGroup>
                <NavSectionHeader className={styles.subSectionHeader}>
                  {t('settings.section.organization', 'Organization')}
                </NavSectionHeader>
                <NavSubItem
                  value="settings-switch-organization"
                  onClick={() => handleNavigate('/settings/switch-organization')}
                >
                  {t('organizations.switch-title', 'Switch Organization')}
                </NavSubItem>
                <NavSubItem value="settings-locations" onClick={() => handleNavigate('/settings/locations')}>
                  {t('locations.title', 'Locations')}
                </NavSubItem>
                <NavSectionHeader className={styles.subSectionHeader}>
                  {t('admin.settings.section.configuration', 'Configuration')}
                </NavSectionHeader>
                {settingsSectionAllowed('category', role) && (
                  <NavSubItem value="settings-category" onClick={() => handleNavigate('/settings/category')}>
                    {t('config-item.categories', 'Categories')}
                  </NavSubItem>
                )}
                {settingsSectionAllowed('attributes', role) && (
                  <NavSubItem value="settings-attributes" onClick={() => handleNavigate('/settings/attributes')}>
                    {t('config-item.attributes', 'Attributes')}
                  </NavSubItem>
                )}
                <NavSubItem value="settings-brand" onClick={() => handleNavigate('/settings/brand')}>
                  {t('config-item.brands', 'Brands')}
                </NavSubItem>
                <NavSubItem value="settings-product-line" onClick={() => handleNavigate('/settings/product-line')}>
                  {t('config-item.product-lines', 'Product Lines')}
                </NavSubItem>
                {TAGS_UI_ENABLED && (
                  <NavSubItem value="settings-tags" onClick={() => handleNavigate('/settings/tags')}>
                    {t('config-item.tags', 'Tags')}
                  </NavSubItem>
                )}
              </NavSubItemGroup>
            </NavCategory>
          </>
        )}

        {isAdmin && (
          <>
            {/* Admin Category with Sub-Items */}
            <NavCategory value="admin-category">
              <NavCategoryItem icon={<AdminIcon />}>{t('navigation.admin', 'Admin')}</NavCategoryItem>
              <NavSubItemGroup>
                <NavSubItem value="admin-api-keys" onClick={() => handleNavigate('/admin/api-keys')}>
                  {t('admin.api-keys.title', 'API Keys')}
                </NavSubItem>
                <NavSubItem value="admin-feature-flags" onClick={() => handleNavigate('/admin/feature-flags')}>
                  {t('admin.feature-flags.title', 'Feature Flags')}
                </NavSubItem>
                <NavSubItem value="admin-permissions" onClick={() => handleNavigate('/admin/permissions')}>
                  {t('admin.permissions.title', 'Permissions')}
                </NavSubItem>
                <NavSubItem value="admin-ai-metrics" onClick={() => handleNavigate('/admin/ai-metrics')}>
                  {t('admin.metrics.title', 'AI Metrics')}
                </NavSubItem>
                <NavSubItem value="inventory-overview" onClick={() => handleNavigate('/admin/inventory-overview')}>
                  {t('admin.inventory-overview.title', 'Inventory Activity')}
                </NavSubItem>
                <NavSubItem value="admin-algolia" onClick={() => handleNavigate('/admin/algolia')}>
                  {t('admin.algolia.title', 'Algolia Search & Sync')}
                </NavSubItem>
              </NavSubItemGroup>
            </NavCategory>
          </>
        )}
      </NavDrawerBody>
      <NavDrawerFooter>
        <AppVersion />
      </NavDrawerFooter>
    </NavDrawer>
  );
}
